import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { GridApi, GetRowIdParams } from "ag-grid-community";
import ExportParticipantsButton from "./ExportParticipantsButton";
import { format } from "date-fns";
import {
    Box,
    Typography,
    Paper,
    TextField,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TuneIcon from "@mui/icons-material/Tune";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import {
    ICellRendererParams,
    ValueGetterParams,
    ValueFormatterParams,
    RowClassParams,
    RowStyle,
} from "ag-grid-community";

import { CustomGridColDef, DataGridCustom } from "../../common/Datagrid";
import { ActivityBookingDetails, Passenger } from "../model/ActivityBookingDetails";
import { getActivityBookingDetails } from "../routes/getActivityBookings";
import BookingDetailModal, { SaveTicketPayload } from "./BookingDetailModal";
import EmailCellRenderer from "./emailCellRendering";
import { BookingNote } from "../model/BookingNote";
import { useAuth } from "../../auth/AuthContext/AuthContext";
import { getBookingNotesByExternalId } from "../routes/BookingNotesRoute";
import { insertBookingTicket } from "../routes/TicketRoute";
import { toast } from "react-toastify";
import { joinRoom, leaveRoom, onSocketEvent } from "../../auth/AuthContext/socketManager";
import { BookingSignal, SendBookingStatus, TERMINAL_PROCESS_STATUSES, normalizeProcessStatus } from "../../../api/socketStatusModel";
import { mergeBookingRefresh } from "../model/booking-flow-utils";

/* ===================== INTERFACCE ===================== */

interface GridRow extends ActivityBookingDetails {
    id: number;
    Passengers: Passenger[] | null
}

interface Props {
    darkMode: boolean;
    dateFilter?: (b: GridRow) => boolean;
}

const getBookingRowId = ({ data }: GetRowIdParams<GridRow>): string => String(data.id);

// Dati per l'aggiornamento del processo ticket
interface TicketStatusPayload {
    ExternalActivityId: string;
    step: number;
    message: string;
    ticketNumber?: string;
    pdfLink?: string;
    audioGuideLink?: string;
    error?: boolean;
}

type ProcessState = {
    status: string;
    message?: string;
    error?: string;
    updatedAt: number;
};
/*
const getBookingIdentifiers = (booking: Partial<ActivityBookingDetails> | null | undefined): string[] => {
    if (!booking) return [];

    return [booking.ExternalBookingId]
        .filter((value): value is string => Boolean(value));
};

const getBookingIdentifiers = (booking: Partial<ActivityBookingDetails> | null | undefined): string[] => {
    if (!booking) return [];

    return [booking.ExternalBookingId, booking.ExternalActivityId]
        .map(val => (val !== null && val !== undefined ? String(val).trim() : ""))
        .filter(val => val !== "");
};
*/
const getBookingIdentifiers = (booking: Partial<ActivityBookingDetails> | null | undefined): string[] => {
    if (!booking) return [];

    return [booking.ExternalBookingId, booking.ExternalActivityId]
        .filter((value): value is string => Boolean(value))
        .map(String)
        .map(s => s.trim());
};

const matchesBookingIdentifier = (booking: Partial<ActivityBookingDetails> | null | undefined, candidate: string | null | undefined) => {
    if (!booking || !candidate) return false;
    return getBookingIdentifiers(booking).includes(candidate);
};

const stringifySearchValue = (value: unknown): string => {
    if (value == null) return "";
    if (value instanceof Date) return value.toISOString();
    if (Array.isArray(value)) return value.map(stringifySearchValue).join(" ");
    if (typeof value === "object") return Object.values(value).map(stringifySearchValue).join(" ");
    return String(value);
};

const rowMatchesSearch = (row: GridRow, searchTerm: string): boolean => {
    const searchWords = searchTerm
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);
    if (searchWords.length === 0) return true;

    const searchableRow = Object.values(row)
        .map(stringifySearchValue)
        .join(" ")
        .toLowerCase();

    return searchWords.every(word => searchableRow.includes(word));
};

const createProcessState = (state: { status: string; message?: string; error?: string }): ProcessState => ({
    ...state,
    updatedAt: Date.now(),
});

const getProgressProcessState = (event: string, data: { message?: string; error?: string }) => {
    // Usa l'evento esattamente come arriva dal backend
    const eventKey = event && typeof event === 'string' ? event.trim().toUpperCase() : '';
    // Mappa di messaggi per ogni stato
    const eventMessages: Record<string, string> = {
        TICKET_SAVED: "Dati salvati. Sto preparando l'invio dell'email...",
        TICKET_DOWNLOAD_STARTED: "Preparazione download del ticket...",
        TICKET_DOWNLOADING: "Sto scaricando il biglietto da allegare all'email...",
        DOWNLOAD_STARTED: "Sto scaricando il biglietto da allegare all'email...",
        TICKET_DOWNLOAD_COMPLETED: "Ticket scaricato con successo.",
        DOWNLOAD_COMPLETED: "Ticket scaricato con successo.",
        AUDIO_GUIDE_DOWNLOAD_STARTED: "Preparazione download dell'audioguida...",
        AUDIO_GUIDE_DOWNLOADING: "Download dell'audioguida in corso...",
        AUDIO_GUIDE_DOWNLOAD_COMPLETED: "Audioguida scaricata con successo.",
        SENDING_EMAIL: "Invio email in corso...",
        EMAIL_SENT: "Email inviata con successo.",
        COMPLETED: "Operazione completata con successo.",
        AUDIO_GUIDE_DOWNLOAD_FAILED: "Errore nel download dell'audioguida.",
        FAILED: "Errore durante il processo.",
        ERROR: "Errore durante il processo.",
        TICKET_ERROR: "Errore durante la generazione del ticket.",
        PROCESSING: "Elaborazione in corso..."
    };

    // Se il backend invia un messaggio custom, usalo, altrimenti prendi quello standard
    const message = data.message || data.error || eventMessages[eventKey] || "Operazione in corso...";
    return {
        status: eventKey || SendBookingStatus.PROCESSING,
        message,
        error: data.error,
    };
};

/* ===================== TICKET SUMMARY RENDERER ===================== */

const getTicketPeopleTotal = (ticketSummary: unknown): number | null => {
    if (typeof ticketSummary !== "string") return null;

    const quantities = ticketSummary
        .split(",")
        .map(part => part.trim().match(/(\d+)\s*$/));

    if (quantities.length === 0 || quantities.some(match => match === null)) return null;
    return quantities.reduce((total, match) => total + Number(match![1]), 0);
};

const TicketSummaryColorRenderer: React.FC<ICellRendererParams<GridRow>> = ({ data, value }) => {
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
    if (!value) return null;

    const hasTicket = !!data?.TicketNumber;
    const sent = data?.ticketIsSentToCustomer;
    const peopleTotal = getTicketPeopleTotal(value) ?? data?.Quantity ?? null;

    let bg = "rgba(255, 166, 0, 1)";
    if (hasTicket && !sent) bg = "#FFFFFF"; //rgba(255, 235, 59, 1)";
    if (hasTicket && sent) bg = "rgba(22, 241, 29, 1)";

    if (isPhone) {
        return (
            <Box
                title={String(value)}
                aria-label={`Ticket: ${String(value)}${peopleTotal !== null ? `. Totale persone: ${peopleTotal}` : ""}`}
                sx={{
                    width: 42,
                    height: 24,
                    mx: "auto",
                    my: "3px",
                    backgroundColor: bg,
                    color: "#000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                }}
            >
                {peopleTotal !== null && (
                    <Typography component="span" sx={{ fontSize: "0.8rem", fontWeight: 700, lineHeight: 1 }}>
                        {peopleTotal}
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{
            width: "100%",
            height: "100%",
            backgroundColor: bg,
            color: "#000",
            display: "flex",
            alignItems: "center",
            borderRadius: "4px",
            px: 1
        }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {value}
            </Typography>
        </Box>
    );
};

const BookingStatusRenderer: React.FC<ICellRendererParams<GridRow>> = ({ value }) => {
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
    const status = String(value ?? "").toUpperCase();
    const isConfirmed = status === "CONFIRMED";
    const isCancelled = status === "CANCELLED";

    if (isPhone) {
        return (
            <Box
                title={status}
                aria-label={`Stato: ${status}`}
                sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
                {isConfirmed ? (
                    <CheckCircleIcon sx={{ color: "#2eaf4a", fontSize: 21 }} />
                ) : isCancelled ? (
                    <CancelIcon sx={{ color: "#d32f2f", fontSize: 21 }} />
                ) : (
                    <Typography variant="caption" noWrap>{status}</Typography>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ height: "100%", width: "100%", minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{
                width: "100%",
                minWidth: 0,
                height: "100%",
                backgroundColor: isConfirmed ? "rgba(46, 125, 50, 0.7)" : "rgba(241, 89, 89, 0.5)",
                color: isConfirmed ? "#2dd135ff" : "#940101ff",
                fontSize: "0.7rem",
                fontWeight: "bold",
                textAlign: "center",
                borderRadius: "4px",
                px: 1,
                boxSizing: "border-box",
                overflow: "hidden",
            }}>
                <Typography
                    component="span"
                    title={status}
                    sx={{
                        display: "block",
                        width: "100%",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        font: "inherit",
                    }}
                >
                    {status}
                </Typography>
            </Box>
        </Box>
    );
};

/* ===================== COLONNE ===================== */

const columns: CustomGridColDef[] = [
    { field: "OTA", headerName: "OTA", minWidth: 60, context: { importance: 2 } },
    {
        field: "StartDateTime",
        headerName: "Data",
        minWidth: 105,
        context: { importance: 1, mobileWidth: 105 },
        valueGetter: (p: ValueGetterParams<GridRow>) =>
            p.data?.StartDateTime ? new Date(p.data.StartDateTime) : null,
        valueFormatter: (p: ValueFormatterParams<GridRow>) =>
            p.value ? format(new Date(p.value), "dd/MM/yyyy HH:mm") : "",
        cellRenderer: (p: ICellRendererParams<GridRow, Date>) => {
            const formattedDate = p.value ? format(new Date(p.value), "dd/MM/yyyy HH:mm") : "";
            return (
                <span
                    title={formattedDate}
                    style={{
                        display: "block",
                        width: "100%",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        direction: "rtl",
                        textAlign: "left",
                        unicodeBidi: "plaintext",
                    }}
                >
                    {formattedDate}
                </span>
            );
        },
    },
    /*{
        field: "StartDateTime",
        headerName: "Data",
        minWidth: 170,
        valueGetter: (p: ValueGetterParams<GridRow>) => {
            if (!p.data?.StartDateTime) return null;
    
            // 1. Prendiamo la stringa originale (es: "2026-03-29T16:30:00Z")
            const rawDate = p.data.StartDateTime;
    
            // 2. Rimuoviamo la 'Z' o eventuali offset +00:00 per forzare il tempo locale
            // Questo impedisce a JS di aggiungere le 2 ore dell'ora legale
            const localDateString = rawDate.replace(/Z$|[+-]\d{2}:\d{2}$/, '');
    
            return new Date(localDateString);
        },
        valueFormatter: (p: ValueFormatterParams<GridRow>) =>
            p.value ? format(p.value, "dd/MM/yyyy HH:mm") : "",
    },*/
    {
        field: "ProductConfirmationCode",
        headerName: "Codice (Bokun)",
        minWidth: 85,
        context: { importance: 1, mobileWidth: 85 },
        cellRenderer: (p: ICellRendererParams<GridRow, string>) => {
            const code = p.data?.ProductConfirmationCode;
            const link = p.data?.LinkBokun;
            if (!code) return null;
            return link ? (
                <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{
                        textDecoration: "underline",
                        borderRadius: "4px",
                        cursor: "pointer",
                        color: "#A0C4FF",
                        fontWeight: "bold",
                        fontSize: "0.72rem",
                    }}
                >
                    {code}
                </a>
            ) : (
                <span>{code}</span>
            );
        }
    },
    { field: "externalId", headerName: "Prodotto", minWidth: 90, context: { importance: 2 } },
    {
        field: "BookingStatus",
        headerName: "Stato",
        minWidth: 80,
        context: { importance: 1, mobileWidth: 52 },
        cellRenderer: BookingStatusRenderer,
    },
    { field: "TicketsSummary", headerName: "Ticket", minWidth: 90, cellRenderer: TicketSummaryColorRenderer, context: { importance: 1, mobileWidth: 52 } },
    { field: "LastName", headerName: "Cognome", minWidth: 130, context: { importance: 2 } },
    { field: "FirstName", headerName: "Nome", minWidth: 120, context: { importance: 2 } },
    { field: "Title", headerName: "Titolo", minWidth: 260, context: { importance: 3, wideDesktopOnly: true } },
    { field: "Email", headerName: "Email", width: 145, minWidth: 120, maxWidth: 165, cellRenderer: EmailCellRenderer, context: { importance: 3 } },
    { field: "PhoneNumber", headerName: "Telefono", minWidth: 140, context: { importance: 3 } },
    { field: "ChannelType", headerName: "Canale", minWidth: 90, hide: true, context: { importance: 3 } },
];

/* ===================== COMPONENTE ===================== */

const ActivityBookingsList: React.FC<Props> = ({ darkMode, dateFilter }) => {

    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

    const [rows, setRows] = useState<GridRow[]>([]);
    const [loading, setLoading] = useState(true);
    const rowsRef = useRef<GridRow[]>([]);
    const exportGridApi = useRef<GridApi<GridRow> | null>(null);
    const refreshSequence = useRef(0);
    const refreshInProgress = useRef(false);
    const updatesDuringRefresh = useRef<ActivityBookingDetails[]>([]);
    const reconnectRefresh = useRef<() => void>(() => {});

    const [currentBooking, setCurrentBooking] = useState<GridRow | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const currentBookingRef = useRef<GridRow | null>(null);

    const [notes, setNotes] = useState<BookingNote[]>([]);
    const [ticketNumber, setTicketNumber] = useState("");
    const [ticketSent, setTicketSent] = useState(false);
    const [pdfLink, setPdfLink] = useState("");
    const [audioGuideLink, setAudioGuideLink] = useState("");
    const [isSavingTicket, setIsSavingTicket] = useState(false);

    const [search, setSearch] = useState("");
    const [channelFilter, setChannelFilter] = useState("ALL");
    const [otaFilter, setOtaFilter] = useState("ALL");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [onlyConfirmed, setOnlyConfirmed] = useState(false);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    const defaultFromDate = format(new Date(), "yyyy-MM-dd");
    const [fromDate, setFromDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
    const [toDate, setToDate] = useState<string>("");
    const fromDateRef = useRef(fromDate);
    const toDateRef = useRef(toDate);

    const [tkFilter, setTkFilter] = useState("ALL");
    const fromDateInputRef = useRef<HTMLInputElement | null>(null);
    const toDateInputRef = useRef<HTMLInputElement | null>(null);

    const openNativeDatePicker = (input: HTMLInputElement | null) => {
        if (!input) return;

        input.focus();
        const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
        if (pickerInput.showPicker) {
            pickerInput.showPicker();
            return;
        }

        input.click();
    };

    const { isAuthenticated } = useAuth();
    const memoColumns = useMemo(() => columns, []);
    // Aggiunga questa riga insieme agli altri stati (come modalOpen, currentBooking, ecc.)
    // ✅ POSIZIONE CORRETTA: All'interno della funzione del componente
    const [processingStates, setProcessingStates] = useState<Record<string, ProcessState>>({});
    /* von data di oggi preimpostate, ma possiamo anche lasciare vuote e far scegliere all'utente:
        const [fromDate, setFromDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
        const [toDate, setToDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
    */

    const { userId, userName } = useAuth();
    const userIdRef = useRef<string | null>(userId);
    const rollbackSentStatusRef = useRef(new Set<string>());

    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    useEffect(() => {
        currentBookingRef.current = currentBooking;
    }, [currentBooking]);

    const currentExternalBookingId = currentBooking?.ExternalBookingId;
    const currentExternalActivityId = currentBooking?.ExternalActivityId;
    const bookingRoomKey = useMemo(() => [currentExternalBookingId, currentExternalActivityId]
        .filter((value): value is string => Boolean(value))
        .map(String)
        .map(value => value.trim())
        .join("\u0000"), [currentExternalBookingId, currentExternalActivityId]);

    useEffect(() => {
        if (!modalOpen || !bookingRoomKey) return;

        const bookingIdentifiers = bookingRoomKey ? bookingRoomKey.split("\u0000") : [];
        bookingIdentifiers.forEach(joinRoom);

        return () => {
            bookingIdentifiers.forEach(leaveRoom);
        };
    }, [modalOpen, bookingRoomKey]);

    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    useEffect(() => {
        fromDateRef.current = fromDate;
        toDateRef.current = toDate;
    }, [fromDate, toDate]);

    const rollbackTicketSentStatus = async (bookingId: string | null | undefined) => {
        if (!bookingId || rollbackSentStatusRef.current.has(bookingId)) return;

        rollbackSentStatusRef.current.add(bookingId);

        const matchingBooking =
            (currentBookingRef.current && matchesBookingIdentifier(currentBookingRef.current, bookingId)
                ? currentBookingRef.current
                : null) ?? rowsRef.current.find(row => matchesBookingIdentifier(row, bookingId));

        setRows(prev => prev.map(row =>
            matchesBookingIdentifier(row, bookingId)
                ? { ...row, ticketIsSentToCustomer: false }
                : row
        ));

        setCurrentBooking(prev => {
            if (!matchesBookingIdentifier(prev, bookingId)) return prev;
            return prev
                ? {
                    ...prev,
                    ticketIsSentToCustomer: false,
                }
                : prev;
        });

        if (matchesBookingIdentifier(currentBookingRef.current, bookingId)) {
            setTicketSent(false);
        }

        if (!matchingBooking?.ExternalActivityId || !matchingBooking.TicketNumber) {
            return;
        }

        try {
            await insertBookingTicket(
                matchingBooking.ExternalActivityId,
                matchingBooking.TicketNumber,
                false,
                matchingBooking.PdfTicketLink || "",
                matchingBooking.PdfAudioGuideLink || "",
                matchingBooking.ticketLanguage || "en",
                userIdRef.current || localStorage.getItem("userId") || "0"
            );
        } catch (error) {
            console.error("Errore durante il rollback dello stato ticket:", error);
        }
    };
    const currentProcessStatus = useMemo(() => {
        const bookingKeys = getBookingIdentifiers(currentBooking);
        let latestState: ProcessState | undefined;

        for (const key of bookingKeys) {
            const nextState = processingStates[key];
            if (nextState && (!latestState || nextState.updatedAt > latestState.updatedAt)) {
                latestState = nextState;
            }
        }

        return latestState;
    }, [currentBooking, processingStates]);
    /*
        useEffect(() => {
            (async () => {
                setLoading(true);
                try {
                    const toDay = new Date().toISOString().split('T')[0]; // Genera "2026-03-26"
                    // Passiamo undefined per il primo parametro (bookingId) e 'oggi' per il secondo
                    const data = await getActivityBookingDetails(undefined, toDay);
                    setRows(data.map(b => ({ ...b, id: b.BookingId })));
                } finally {
                    setLoading(false);
                }
            })();
        }, []);
    */

    // Modifica lo useEffect per reagire al cambio delle date
    /* useEffect(() => {
         (async () => {
             setLoading(true);
             try {
                 // Passiamo le date alla tua rotta API
                 // Nota: verifica se getActivityBookingDetails supporta il range, 
                 // altrimenti filtreremo localmente in filteredRows
                 const data = await getActivityBookingDetails(undefined, undefined, fromDate, toDate);
                 setRows(data.map(b => ({ ...b, id: b.BookingId })));
             } catch (error) {
                 console.error("Errore recupero dati:", error);
             } finally {
                 setLoading(false);
             }
         })();
     }, [fromDate, toDate]); // Si ricarica quando cambiano le date
     */

    const refreshData = useCallback(async (background = false): Promise<void> => {
        const sequence = ++refreshSequence.current;
        refreshInProgress.current = true;
        updatesDuringRefresh.current = [];
        if (!background) setLoading(true);
        try {
            const adjustedToDate = toDate ? toDate + "T23:59:59" : toDate;
            const data = await getActivityBookingDetails(undefined, undefined, fromDate, adjustedToDate);
            if (sequence !== refreshSequence.current) return;
            let nextRows: GridRow[] = data.map(b => ({ ...b, id: b.BookingId }));
            for (const update of updatesDuringRefresh.current) {
                nextRows = mergeBookingRefresh(nextRows, update, fromDate, toDate);
            }
            setRows(nextRows);
            setCurrentBooking(current => current
                ? nextRows.find(row => String(row.ExternalActivityId) === String(current.ExternalActivityId)) ?? current
                : current);
        } catch (error) {
            console.error("Errore recupero dati:", error);
            if (background && sequence === refreshSequence.current) {
                toast.warning("Connessione ripristinata, ma il recupero delle prenotazioni non è riuscito. Ricarica la pagina.");
            }
        } finally {
            if (sequence === refreshSequence.current) {
                refreshInProgress.current = false;
                updatesDuringRefresh.current = [];
                setLoading(false);
            }
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        reconnectRefresh.current = () => { void refreshData(true); };
    }, [refreshData]);

    // Questo serve per ricaricare quando l'utente cambia i filtri data
    useEffect(() => {
        void refreshData();
    }, [refreshData]);

    const handleResetDates = () => {
        setFromDate(defaultFromDate);
        setToDate("");
    };

    // ==================== LISTENER SOCKET.IO ====================
    useEffect(() => {
        if (!isAuthenticated) return;

        // 1. DEFINIZIONE DELLA FUNZIONE HELPER
        const setProcessingState = (bookingKey: string | null | undefined, nextState: { status: string; message?: string; error?: string }) => {
            if (!bookingKey) return;

            const normalizedNextStatus = normalizeProcessStatus(nextState.status);

            setProcessingStates(prev => {
                const nextStates = { ...prev };
                const matchingBooking =
                    (currentBookingRef.current && matchesBookingIdentifier(currentBookingRef.current, bookingKey)
                        ? currentBookingRef.current
                        : null) ?? rowsRef.current.find(row => matchesBookingIdentifier(row, bookingKey));
                const targetKeys = matchingBooking ? getBookingIdentifiers(matchingBooking) : [bookingKey];

                // BLOCCO: se uno dei targetKeys ha già stato terminale, e il nuovo stato NON è terminale, non aggiornare
                const hasTerminalState = targetKeys.some((key) => {
                    const currentStatus = normalizeProcessStatus(prev[key]?.status);
                    return TERMINAL_PROCESS_STATUSES.has(currentStatus);
                });

                if (hasTerminalState && !TERMINAL_PROCESS_STATUSES.has(normalizedNextStatus)) {
                    // Stato terminale già raggiunto, ignora update non terminali
                    return prev;
                }

                // Se il nuovo stato è terminale, aggiorna normalmente
                const normalizedState = {
                    ...nextState,
                    status: normalizedNextStatus || nextState.status,
                };
                const processState = createProcessState(normalizedState);

                targetKeys.forEach((key) => {
                    nextStates[key] = processState;
                });

                return nextStates;
            });
        };

        // 2. REGISTRAZIONE DI TUTTI I LISTENER
        const unsubscribeGlobalRefresh = onSocketEvent("refresh_bookings", (data: BookingSignal) => {
            console.log(`[Socket] Aggiornamento da ${data.UserId} per ${data.ExternalActivityId}`);
            if (refreshInProgress.current && data.newRow) {
                updatesDuringRefresh.current.push(data.newRow);
            }

            //  toast.info(`🔄 Prenotazione aggiornata da ${data.UserId}`, {
            //      position: "top-right",
            //      autoClose: 2000
            //  });

            setRows(prevRows => mergeBookingRefresh(
                prevRows,
                data.newRow,
                fromDateRef.current,
                toDateRef.current,
            ));

            setCurrentBooking(prevCurrent => {
                if (prevCurrent && matchesBookingIdentifier(prevCurrent, data.ExternalActivityId)) {
                    return { ...data.newRow, id: data.newRow.BookingId };
                }
                return prevCurrent;
            });
        });

        // --- Listener Originali ---
        const unsubscribeStatus = onSocketEvent("ticket:status", (data: TicketStatusPayload) => {
            const toastId = "ticket-process-toast";

            setProcessingState(data.ExternalActivityId, {
                status: data.error ? 'ERROR' : data.step === 3 ? 'COMPLETED' : 'PROCESSING',
                message: data.message,
                error: data.error ? data.message : undefined
            });

            if (data.error) {
                void rollbackTicketSentStatus(data.ExternalActivityId);
                toast.update(toastId, { render: `❌ ${data.message}`, type: "error", isLoading: false, autoClose: 5000 });
            } else {
                toast.update(toastId, {
                    render: `${data.step === 3 ? '✅' : '⚙️'} ${data.message}`,
                    isLoading: data.step < 3,
                    type: data.step === 3 ? "success" : "default",
                    autoClose: data.step === 3 ? 5000 : false
                });
            }

            if (data.step === 3 && !data.error) {
                setRows(prev => prev.map(row => {
                    if (matchesBookingIdentifier(row, data.ExternalActivityId)) {
                        return {
                            ...row,
                            TicketNumber: data.ticketNumber ?? null,
                            PdfTicketLink: data.pdfLink ?? row.PdfTicketLink ?? "",
                            PdfAudioGuideLink: data.audioGuideLink ?? row.PdfAudioGuideLink ?? "",
                            ticketIsSentToCustomer: true
                        } as GridRow;
                    }
                    return row;
                }));

                setCurrentBooking(prev => {
                    if (!matchesBookingIdentifier(prev, data.ExternalActivityId)) return prev;
                    return prev
                        ? {
                            ...prev,
                            TicketNumber: data.ticketNumber ?? prev.TicketNumber,
                            PdfTicketLink: data.pdfLink ?? prev.PdfTicketLink,
                            PdfAudioGuideLink: data.audioGuideLink ?? prev.PdfAudioGuideLink,
                            ticketIsSentToCustomer: true,
                        }
                        : prev;
                });
            }
        });

        const unsubscribeProgress = onSocketEvent("progress", (payload: { event: string, data: any }) => {
            const { event, data } = payload;
            const normalizedEvent = normalizeProcessStatus(event);
            const toastId = `ticket-process-${data.ExternalActivityId}`;
            const nextProcessState = getProgressProcessState(normalizedEvent, data);

            setProcessingState(data.ExternalActivityId, {
                status: nextProcessState.status,
                message: nextProcessState.message,
                error: nextProcessState.error,
            });

            const isIntermedia = new Set<string>([
                SendBookingStatus.DOWNLOAD_STARTED,
                SendBookingStatus.DOWNLOAD_COMPLETED,
                SendBookingStatus.TICKET_DOWNLOAD_STARTED,
                SendBookingStatus.TICKET_DOWNLOAD_COMPLETED,
                SendBookingStatus.AUDIO_GUIDE_DOWNLOAD_STARTED,
                SendBookingStatus.AUDIO_GUIDE_DOWNLOAD_COMPLETED,
            ]).has(normalizedEvent);

            if (isIntermedia) {
                toast.info(nextProcessState.message || `⚙️ ${event}`, {
                    toastId,
                    isLoading: true,
                    closeButton: false,
                    autoClose: false
                });
            } else if (normalizedEvent === SendBookingStatus.COMPLETED || normalizedEvent === SendBookingStatus.EMAIL_SENT) {
                setRows(prev => prev.map(row =>
                    matchesBookingIdentifier(row, data.ExternalActivityId)
                        ? {
                            ...row,
                            ticketIsSentToCustomer: true,
                            PdfTicketLink: data.pdfLink ?? row.PdfTicketLink,
                            PdfAudioGuideLink: data.audioGuideLink ?? row.PdfAudioGuideLink,
                        }
                        : row
                ));

                setCurrentBooking(prev => {
                    if (!matchesBookingIdentifier(prev, data.ExternalActivityId)) return prev;
                    return prev
                        ? {
                            ...prev,
                            ticketIsSentToCustomer: true,
                            PdfTicketLink: data.pdfLink ?? prev.PdfTicketLink,
                            PdfAudioGuideLink: data.audioGuideLink ?? prev.PdfAudioGuideLink,
                        }
                        : prev;
                });

                toast.update(toastId, {
                    render: `✅ ${nextProcessState.message || 'Email inviata correttamente'}`,
                    type: "success",
                    isLoading: false,
                    autoClose: 5000
                });
            } else if (
                normalizedEvent === SendBookingStatus.FAILED ||
                normalizedEvent === SendBookingStatus.ERROR ||
                normalizedEvent === SendBookingStatus.TICKET_ERROR ||
                normalizedEvent === SendBookingStatus.AUDIO_GUIDE_DOWNLOAD_FAILED
            ) {
                void rollbackTicketSentStatus(data.ExternalActivityId);
                toast.update(toastId, {
                    render: `❌ ${nextProcessState.message || 'Errore nel processo'}`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000
                });
            }
        });

        const unsubscribeBookingStatus = onSocketEvent("booking_status_update", (data: { ExternalActivityId: string, status: string, error?: string }) => {
            setProcessingState(data.ExternalActivityId, { status: normalizeProcessStatus(data.status), error: data.error });
        });

        // 4. Listener per note in tempo reale
        const unsubscribeNote = onSocketEvent("booking_note", async (data: { ExternalBookingId: number, message: string, timestamp: string }) => {
            const currentBooking = currentBookingRef.current;
            if (!currentBooking) return;

            // Aggiorna le note solo se la nota appartiene alla prenotazione attualmente aperta
            if (String(currentBooking.ExternalBookingId) === String(data.ExternalBookingId)) {
                try {
                    const existingNotes = await getBookingNotesByExternalId(String(data.ExternalBookingId));
                    setNotes(existingNotes);
                } catch (error) {
                    console.error("Errore aggiornamento note via socket:", error);
                }
            }
        });

        // 3. UNICA FUNZIONE DI CLEANUP ALLA FINE DEL COMPONENTE
        const unsubscribeReconnect = onSocketEvent("connect", () => reconnectRefresh.current());
        return () => {
            unsubscribeReconnect();
            refreshSequence.current += 1;
            refreshInProgress.current = false;
            updatesDuringRefresh.current = [];
            unsubscribeStatus();
            unsubscribeProgress();
            unsubscribeBookingStatus();
            unsubscribeGlobalRefresh();
            unsubscribeNote();
        };
    }, [isAuthenticated]);
    // ==================== FINE LISTENER =========================

    const rowsFilteredByDate = useMemo(() => {
        return dateFilter ? rows.filter(b => dateFilter(b)) : rows;
    }, [rows, dateFilter]);

    const channels = useMemo(() => {
        return Array.from(new Set(rows.map(r => r.ChannelType).filter(Boolean)))
            .map(String)
            .sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const otas = useMemo(() => {
        return Array.from(new Set(rows.map(r => r.OTA).filter(Boolean)))
            .map(String)
            .sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const tks = useMemo(() => {
        return Array.from(new Set(rowsFilteredByDate.map(r => r.externalId).filter(Boolean)))
            .map(String)
            .sort((a, b) => a.localeCompare(b));
    }, [rowsFilteredByDate]);

    const filteredRows = useMemo(() => {
        return rows
            .filter(b => (dateFilter ? dateFilter(b) : true))
            .filter(b => channelFilter === "ALL" || b.ChannelType === channelFilter)
            .filter(b => otaFilter === "ALL" || b.OTA === otaFilter)
            .filter(b => tkFilter === "ALL" || b.externalId === tkFilter) // <-- Nuovo filtro
            .filter(b => !onlyConfirmed || b.BookingStatus === 'CONFIRMED')
            .filter(b => {
                if (statusFilter === "ALL") return true;
                const hasTicket = !!b.TicketNumber;
                const sent = b.ticketIsSentToCustomer;
                if (statusFilter === "MISSING") return !hasTicket;
                if (statusFilter === "TO_SEND") return hasTicket && !sent;
                if (statusFilter === "SENT") return hasTicket && sent;
                return true;
            })
            .filter(b => rowMatchesSearch(b, search))
            .sort((a, b) => {
                const dateA = a.StartDateTime ? new Date(a.StartDateTime).getTime() : 0;
                const dateB = b.StartDateTime ? new Date(b.StartDateTime).getTime() : 0;
                return dateA - dateB;
            });
    }, [rows, search, dateFilter, channelFilter, otaFilter, tkFilter, statusFilter, onlyConfirmed]);
    /*
        const handleRowClick = async (row: GridRow) => {
            setCurrentBooking(row);
            setTicketNumber(row.TicketNumber ?? "");
            setTicketSent(row.ticketIsSentToCustomer ?? false);
            setNotes([]);
            setModalOpen(true);
        };
    */
    const handleRowClick = async (row: GridRow) => {
        setCurrentBooking(row);
        setTicketNumber(row.TicketNumber ?? "");
        setTicketSent(row.ticketIsSentToCustomer ?? false);
        setPdfLink(row.PdfTicketLink ?? "");
        setAudioGuideLink(row.PdfAudioGuideLink ?? "");
        // Inizializziamo a vuoto per evitare di vedere le note della prenotazione precedente
        setNotes([]);

        try {
            // Recuperiamo le note reali dal database Azure
            if (row.ExternalBookingId) {
                const existingNotes = await getBookingNotesByExternalId(row.ExternalBookingId.toString());
                setNotes(existingNotes);
            }
        } catch (error) {
            console.error("Errore nel recupero note dal database:", error);
            // Opzionale: notifica all'utente che il diario di bordo è momentaneamente offline
        }

        setModalOpen(true);
    };

    const handleAddNote = async (message: string) => {
        if (!currentBooking) return;
        const newNote: BookingNote = {
            id: Date.now(),
            ExternalBookingId: currentBooking.ExternalBookingId,
            UserId: userId || "0",
            lastName: userName?.split(' ')[1] || "",
            firstName: userName?.split(' ')[0] || "",
            message,
            createdAt: new Date(),
        };
        setNotes(prev => [newNote, ...prev]);
    };

    const handleSendAndSave = async ({ ticketNumber: nextTicketNumber, pdfLink: nextPdfLink, audioGuideLink: nextAudioGuideLink, ticketSent: nextTicketSent, ...rest }: SaveTicketPayload & { ticketLanguage?: string }) => {
        try {
            if (!currentBooking) {
                throw new Error("Prenotazione non selezionata.");
            }

            const nextTicketLanguage = rest.ticketLanguage ?? currentBooking.ticketLanguage ?? "en";

            const externalActivityId = currentBooking.ExternalActivityId;

            if (!nextTicketNumber.trim() || !externalActivityId) {
                throw new Error("ExternalActivityId mancante per la prenotazione selezionata.");
            }

            setIsSavingTicket(true);
            setTicketNumber(nextTicketNumber);
            setPdfLink(nextPdfLink);
            setAudioGuideLink(nextAudioGuideLink);
            if (nextTicketSent) {
                getBookingIdentifiers(currentBooking).forEach(identifier => rollbackSentStatusRef.current.delete(identifier));
            } else {
                setTicketSent(false);
            }
            setProcessingStates(prev => {
                const nextStates = { ...prev };
                const nextState = createProcessState({
                    status: "SAVING",
                    message: nextTicketSent ? "Salvataggio ticket e avvio invio email..." : "Salvataggio ticket in corso...",
                });
                for (const key of getBookingIdentifiers(currentBooking)) {
                    nextStates[key] = nextState;
                }
                return nextStates;
            });

            // 1. Persistenza ticket tramite ExternalActivityId richiesto dal backend.
            const isSuccessful = await insertBookingTicket(
                externalActivityId,
                nextTicketNumber,
                nextTicketSent,
                nextPdfLink || "",
                nextAudioGuideLink || "",
                nextTicketLanguage,
                userId || localStorage.getItem("userId") || "0"
            );

            if (isSuccessful) {
                const nextLastModifiedBy = userId || localStorage.getItem("userId") || "0";
                // 2. AGGIORNAMENTO GRIGLIA (Quello che faceva il vecchio metodo)
                setRows(prev =>
                    prev.map(r =>
                        r.ExternalBookingId === currentBooking.ExternalBookingId
                            ? {
                                ...r,
                                TicketNumber: nextTicketNumber,
                                ticketIsSentToCustomer: nextTicketSent ? Boolean(r.ticketIsSentToCustomer) : false,
                                PdfTicketLink: nextPdfLink,
                                PdfAudioGuideLink: nextAudioGuideLink,
                                ticketLanguage: nextTicketLanguage,
                                LastModifiedBy: nextLastModifiedBy,
                            }
                            : r
                    )
                );

                setCurrentBooking(prev => prev
                    ? {
                        ...prev,
                        TicketNumber: nextTicketNumber,
                        ticketIsSentToCustomer: nextTicketSent ? Boolean(prev.ticketIsSentToCustomer) : false,
                        PdfTicketLink: nextPdfLink,
                        PdfAudioGuideLink: nextAudioGuideLink,
                        ticketLanguage: nextTicketLanguage,
                        LastModifiedBy: nextLastModifiedBy,
                    }
                    : prev
                );

                if (nextTicketSent) {
                    setProcessingStates(prev => {
                        const nextStates = { ...prev };
                        for (const key of getBookingIdentifiers(currentBooking)) {
                            // Se già terminale, non sovrascrivere
                            const currentStatus = prev[key]?.status;
                            if (currentStatus && TERMINAL_PROCESS_STATUSES.has(normalizeProcessStatus(currentStatus))) {
                                continue;
                            }
                            const nextState = createProcessState({
                                status: SendBookingStatus.PROCESSING,
                                message: "Dati salvati. Sto preparando l'invio dell'email...",
                            });
                            nextStates[key] = nextState;
                        }
                        return nextStates;
                    });
                } else {
                    setProcessingStates(prev => {
                        const nextStates = { ...prev };
                        for (const key of getBookingIdentifiers(currentBooking)) {
                            // Se già terminale, non sovrascrivere
                            const currentStatus = prev[key]?.status;
                            if (currentStatus && TERMINAL_PROCESS_STATUSES.has(normalizeProcessStatus(currentStatus))) {
                                continue;
                            }
                            const nextState = createProcessState({
                                status: SendBookingStatus.COMPLETED,
                                message: "Dati salvati correttamente.",
                            });
                            nextStates[key] = nextState;
                        }
                        return nextStates;
                    });
                }

                // toast.success("✅ Ticket salvato nel database!", { toastId });
                //setModalOpen(false);
            } else {
                throw new Error("Il server non ha confermato il salvataggio.");
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Errore durante il salvataggio nel DB";
            setProcessingStates(prev => {
                const nextStates = { ...prev };
                const nextState = createProcessState({
                    status: "ERROR",
                    message: "Errore durante il salvataggio del ticket.",
                    error: errorMessage,
                });
                for (const key of getBookingIdentifiers(currentBooking)) {
                    nextStates[key] = nextState;
                }
                return nextStates;
            });
            toast.error("❌ Errore durante il salvataggio nel DB");
            console.error(error);
            throw error instanceof Error ? error : new Error(errorMessage);
        } finally {
            setIsSavingTicket(false);
        }
    };

    const getRowStyle = (params: RowClassParams<GridRow>): RowStyle | undefined => {
        if (params.data && params.data.BookingStatus !== "CONFIRMED") {
            return {
                opacity: 0.5,
                fontStyle: 'italic',
                backgroundColor: darkMode ? "rgba(255, 0, 0, 0.02)" : "rgba(255, 0, 0, 0.01)"
            };
        }
        return undefined;
    };

    return (
        <Box sx={{ p: { xs: 0, sm: 2 }, width: "100%", minWidth: 0 }}>
            <Paper sx={{
                p: { xs: 1, sm: 2 },
                height: { xs: "calc(100dvh - 56px)", sm: "calc(100dvh - 96px)", md: "calc(100dvh - 140px)" },
                minHeight: 480,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}>

                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 1.5,
                    mb: 2
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "nowrap" }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", whiteSpace: "nowrap", fontSize: { xs: "1rem", sm: "1.1rem" } }}>
                            Prenotazioni ({filteredRows.length})
                        </Typography>

                        <Box sx={{ flexShrink: 0 }}>
                            <ExportParticipantsButton
                                disabled={loading || filteredRows.length === 0}
                                getRows={() => {
                                    const visible: GridRow[] = [];
                                    const api = exportGridApi.current;
                                    if (!api || api.isDestroyed()) throw new Error("Griglia non pronta per l’estrazione");
                                    api.forEachNodeAfterFilterAndSort(node => {
                                        if (node.data) visible.push(node.data);
                                    });
                                    return visible;
                                }}
                            />
                        </Box>

                    </Box>

                    <Accordion
                        disableGutters
                        elevation={0}
                        expanded={isPhone ? mobileFiltersOpen : true}
                        onChange={(_, expanded) => setMobileFiltersOpen(expanded)}
                        sx={{
                            width: "100%",
                            minWidth: 0,
                            bgcolor: "transparent",
                            border: "none",
                            boxShadow: "none",
                            "&:before": { display: "none" },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="mobile-booking-filters"
                            id="mobile-booking-filters-header"
                            sx={{
                                display: { xs: "flex", sm: "none" },
                                minHeight: 42,
                                px: 1.25,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center", gap: 1 },
                            }}
                        >
                            <TuneIcon fontSize="small" />
                            <Typography sx={{ fontWeight: 600 }}>Filtri</Typography>
                        </AccordionSummary>

                        <AccordionDetails
                            id="mobile-booking-filters"
                            sx={{
                                p: { xs: "12px 0 0", sm: 0 },
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "stretch",
                                gap: 1.5,
                            }}
                        >
                    {/* Container Filtri con lo stile del componente originale */}
                    <Box sx={{ display: dateFilter ? "none" : "flex", gap: 1, alignItems: "center", flexWrap: "wrap", width: "100%" }}>

                        {!dateFilter && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>

                                {/* PICKER "DAL" */}
                                <Box
                                    onClick={() => openNativeDatePicker(fromDateInputRef.current)}
                                    sx={{
                                        minHeight: 40,
                                        px: 1.5,
                                        py: 0.75,
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        position: 'relative',
                                        bgcolor: 'background.paper',
                                        cursor: 'pointer',
                                        flex: { xs: '1 1 100%', sm: '0 1 auto' }
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pointerEvents: 'none', width: '100%' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                            Dal:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {fromDate ? format(new Date(fromDate), "dd/MM/yyyy") : "Seleziona"}
                                        </Typography>
                                        <CalendarMonthIcon fontSize="small" color="action" />
                                    </Box>

                                    <input
                                        ref={fromDateInputRef}
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            opacity: 0,
                                            cursor: 'pointer',
                                            width: '100%',
                                            height: '100%',
                                            appearance: 'none',
                                            pointerEvents: 'none'
                                        }}
                                    />
                                </Box>

                                <Typography color="divider" sx={{ display: { xs: 'none', sm: 'block' } }}>|</Typography>

                                {/* PICKER "AL" */}
                                <Box
                                    onClick={() => openNativeDatePicker(toDateInputRef.current)}
                                    sx={{
                                        minHeight: 40,
                                        px: 1.5,
                                        py: 0.75,
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        position: 'relative',
                                        bgcolor: 'background.paper',
                                        cursor: 'pointer',
                                        flex: { xs: '1 1 100%', sm: '0 1 auto' }
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pointerEvents: 'none', width: '100%' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                            Al:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {toDate ? format(new Date(toDate), "dd/MM/yyyy") : "Seleziona"}
                                        </Typography>
                                        <CalendarMonthIcon fontSize="small" color="action" />
                                    </Box>

                                    <input
                                        ref={toDateInputRef}
                                        type="date"
                                        value={toDate || ""}
                                        onChange={(e) => setToDate(e.target.value)}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            opacity: 0,
                                            cursor: 'pointer',
                                            width: '100%',
                                            height: '100%',
                                            pointerEvents: 'none'
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Reset rapido */}
                        {!dateFilter && (fromDate !== defaultFromDate || toDate !== "") && (
                            <Typography
                                variant="caption"
                                onClick={handleResetDates}
                                sx={{
                                    cursor: "pointer",
                                    color: "error.main",
                                    textDecoration: "underline",
                                    fontWeight: "bold",
                                    ml: 1,
                                    '&:hover': { color: 'error.dark' }
                                }}
                            >
                                Reset Date
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(5, minmax(0, 1fr))" },
                        gap: 1.5,
                        alignItems: "center",
                        width: "100%",
                        "& > .MuiFormControl-root": { minWidth: 0, width: "100%" }
                    }}>

                        <FormControl size="small">
                            <InputLabel id="channel-label">Canale</InputLabel>
                            <Select
                                labelId="channel-label"
                                value={channelFilter}
                                label="Canale"
                                onChange={(e) => setChannelFilter(e.target.value)}
                            >
                                <MenuItem value="ALL">Tutti</MenuItem>
                                {channels.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl size="small">
                            <InputLabel id="ota-label">OTA</InputLabel>
                            <Select
                                labelId="ota-label"
                                value={otaFilter}
                                label="OTA"
                                onChange={(e) => setOtaFilter(e.target.value)}
                            >
                                <MenuItem value="ALL">Tutte</MenuItem>
                                {otas.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl size="small">
                            <InputLabel id="tk-label">TK</InputLabel>
                            <Select
                                labelId="tk-label"
                                value={tkFilter}
                                label="TK (ID)"
                                onChange={(e) => setTkFilter(e.target.value)}
                            >
                                <MenuItem value="ALL">Tutti</MenuItem>
                                {tks.map(tk => (
                                    <MenuItem key={tk} value={tk}>
                                        {tk}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box
                            sx={{
                                position: 'relative',
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                borderRadius: 1,
                                minWidth: 0,
                                width: "100%",
                                '&:hover fieldset': {
                                    borderColor: 'text.primary',
                                }
                            }}
                        >
                            <Box
                                component="fieldset"
                                sx={{
                                    position: 'absolute',
                                    top: '-2px',
                                    inset: 0,
                                    borderRadius: 1.1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    pointerEvents: 'auto',
                                    cursor: 'pointer',
                                    p: 0,
                                    m: 0,
                                }}
                            >
                                <Box
                                    component="legend"
                                    sx={{
                                        px: 0.5,
                                        ml: 1,
                                        visibility: 'visible',
                                        fontSize: '0.7rem',
                                        color: 'text.secondary',
                                        lineHeight: 1,
                                        height: 0,
                                        transform: 'translateY(-10px)',
                                    }}
                                >
                                    Stato
                                </Box>
                            </Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={onlyConfirmed}
                                        onChange={(e) => setOnlyConfirmed(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={onlyConfirmed ? "Solo confermati" : "Tutti"}
                                sx={{ mr: 0.5, ml: 'auto', zIndex: 1, width: 'auto', pr: '5px' }}
                            />
                        </Box>

                        <FormControl size="small">
                            <InputLabel id="status-label">Ticket</InputLabel>
                            <Select
                                labelId="status-label"
                                value={statusFilter}
                                label="Stato Ticket"
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <MenuItem value="ALL">Tutti</MenuItem>
                                <MenuItem value="MISSING" sx={{ bgcolor: "rgba(255, 166, 0, 1)", color: "black" }}>Da acquistare</MenuItem>
                                <MenuItem value="TO_SEND" sx={{ bgcolor: "#FFFFFF", color: "black" }}>Da Inviare</MenuItem>
                                <MenuItem value="SENT" sx={{ bgcolor: "rgba(22, 241, 29, 1)", color: "black" }}>Inviato</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            size="small"
                            placeholder="Cerca..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> }}
                            sx={{
                                gridColumn: "1 / -1",
                                minWidth: 0,
                                width: "100%"
                            }}
                        />
                    </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredRows.length === 0 ? (
                    /* --- MESSAGGIO TABELLA VUOTA --- */
                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        flexGrow: 1,
                        gap: 2,
                        opacity: 0.8
                    }}>
                        <Typography variant="h6" color="text.secondary">
                            Nessuna prenotazione trovata
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Prova a modificare il periodo o a resettare i filtri di ricerca.
                        </Typography>
                        {/* Qui potresti anche mettere il tasto Reset suggerito prima */}
                    </Box>
                ) : (
                    <Box sx={{ flexGrow: 1, width: "100%", minWidth: 0, minHeight: 280, overflow: "hidden" }}>
                        <DataGridCustom
                            getRowId={getBookingRowId}
                            preserveScrollPosition
                            onGridReady={event => { exportGridApi.current = event.api; }}
                            rows={filteredRows}
                            columns={memoColumns}
                            onRowClick={handleRowClick}
                            checkboxSelection={false}
                            themeVariant={darkMode ? "dark" : "light"}
                            getRowStyle={getRowStyle}
                        />
                    </Box>
                )}
            </Paper>

            <BookingDetailModal
                open={modalOpen}
                booking={currentBooking}
                notes={notes}
                ticketNumber={ticketNumber}
                ticketSent={ticketSent}
                pdfLink={pdfLink}
                onPdfLinkChange={setPdfLink}
                audioGuideLink={audioGuideLink}
                onAudioGuideLinkChange={setAudioGuideLink}
                isSavingTicket={isSavingTicket}
                // Per mostrare il caricamento nel tasto "Salva"
                // Sostituiamo (o affianchiamo) i vecchi campi con l'oggetto completo
                processStatus={
                    currentProcessStatus
                }
                webhookStatus={currentProcessStatus?.status}
                onAddNote={handleAddNote}
                onTicketNumberChange={setTicketNumber}
                onTicketSentChange={setTicketSent}
                onSaveTicket={handleSendAndSave}
                onClose={() => setModalOpen(false)}
                lastError={currentProcessStatus?.error}
            />
        </Box>
    );
};

export default ActivityBookingsList;
